/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Polymorphic component utility.
 *
 * This file uses `any` intentionally to work around TypeScript's inability to
 * correlate dynamic element types with their corresponding props and ref types.
 * Type safety is enforced at each component's public API level via generics.
 */

type PolymorphicResult = {
  Component: any;
  props: any;
};

/**
 * Prepares props for polymorphic element rendering.
 *
 * Encapsulates TypeScript's limitation with dynamic element selection and ref
 * typing. When an element type is determined at runtime (via an `as` prop),
 * TypeScript cannot verify ref compatibility because it sees a union of
 * possible element types.
 *
 * @param element - The element type to render (from `as` prop with default set during destructuring)
 * @param props - All props including ref, className, children, etc.
 *
 * @example
 * const { as = 'div', ref, className, ...attrs } = props;
 * const { Component, props: componentProps } = getPolymorphicProps(as, { ref, className, ...attrs, children });
 * return <Component {...componentProps} />;
 */
export function getPolymorphicProps(
  element: keyof React.JSX.IntrinsicElements,
  props: Record<string, unknown>,
): PolymorphicResult {
  return { Component: element, props };
}
